/**
 * `2p` — Configurações globais.
 *
 * 🔒 **Esta tela é a razão de a BR-24 existir.** As duas listas daqui alimentam
 * os dropdowns da plataforma inteira: nada que as leia é campo livre. Um status
 * criado aqui às 9h aparece na `2e` às 9h01 — e é por isso que as mutations
 * invalidam também as chaves de catálogo da API User, que é por onde as telas de
 * módulo leem.
 *
 * 🔒 **Global quer dizer global.** Não há seletor de cliente nesta aba, e o
 * subtítulo diz "efeito em todos os clientes" porque excluir um status aqui muda
 * o vocabulário de todos os tenants ao mesmo tempo.
 *
 * ⚠️ **Excluir é definitivo e não pede confirmação** — como o shot desenha
 * (hover revela a lixeira). Isso é o wireframe, não uma escolha nossa; se o
 * Eduardo quiser um passo de confirmação, é uma linha. Registrado no write-up.
 */
import { useState } from 'react'

import {
  useCatalogoDeServicosAdm,
  useCatalogoDeStatusAdm,
  useCriarServico,
  useCriarStatus,
  useExcluirServico,
  useExcluirStatus,
} from '@/api/queries/administracao'
import { EsqueletoDeChips } from '@/components/esqueleto'
import { EstadoDeErro } from '@/components/estados'
import { BadgeDeStatus, Botao, Entrada } from '@/components/primitivos'
import { usePermission } from '@/permissions/usePermission'

export function ConfiguracoesGlobais() {
  const { editar } = usePermission('admin.configuracoes')

  const status = useCatalogoDeStatusAdm()
  const servicos = useCatalogoDeServicosAdm()
  const criarStatus = useCriarStatus()
  const excluirStatus = useExcluirStatus()
  const criarServico = useCriarServico()
  const excluirServico = useExcluirServico()

  return (
    <section className="grid gap-4 xl:grid-cols-2">
      <CartaoDeCatalogo
        titulo="Config. global — status de oportunidade"
        subtitulo="efeito em todos os clientes"
        podeEditar={editar}
        consulta={status}
        criar={criarStatus}
        excluir={excluirStatus}
        rotuloDeAdicao="novo status"
        itens={
          status.data?.itens.map((s) => ({
            id: s.idStatus,
            rotulo: s.rotulo,
            // Reaproveita o mesmo mapa de tons da `2e`: o chip aqui e o badge lá
            // têm que ser a mesma cor, senão a lista global e a grade contam
            // histórias diferentes sobre o mesmo status.
            chip: <BadgeDeStatus status={s.idStatus} rotulo={s.rotulo} />,
            // 🔒 Os seis status do ciclo do BR-20 são estrutura da plataforma, e
            // o backend recusa excluí-los com 409 ITEM_DO_SISTEMA. Apagar
            // `proposta` levaria junto, por CASCADE, todas as arestas de
            // transição que passam por ele — e recriar o status NÃO recria o
            // grafo; nada na UI nem na API consegue reconstruí-lo.
            //
            // A lixeira some porque oferecer um botão que sempre falha é pior
            // que não oferecer. A checagem que VALE continua sendo a do servidor.
            protegido: s.geridoPeloSistema,
          })) ?? []
        }
        rodape="A ordem e as transições permitidas de cada status vêm do catálogo do servidor — é ele que o formulário 2f usa para saber quais campos exigir em cada fase."
      />

      <CartaoDeCatalogo
        titulo="Config. global — serviços AWS"
        subtitulo="lista dos dropdowns de oportunidade"
        podeEditar={editar}
        consulta={servicos}
        criar={criarServico}
        excluir={excluirServico}
        rotuloDeAdicao="novo serviço"
        itens={
          servicos.data?.itens.map((s) => ({
            id: s.idServico,
            rotulo: s.rotulo,
            chip: (
              <span className="rounded-control border-border-subtle text-text-primary inline-flex items-center border px-3 py-1.5 type-sm">
                {s.rotulo}
              </span>
            ),
          })) ?? []
        }
        rodape="O id do serviço casa com `line_item_product_code` do CUR 2.0 — é o que liga uma oportunidade ao custo real sem tabela de-para."
      />
    </section>
  )
}

type Item = {
  id: string
  rotulo: string
  chip: React.ReactNode
  /**
   * Item que a plataforma gerencia e o servidor recusa excluir (409
   * `ITEM_DO_SISTEMA`). Opcional porque só o catálogo de STATUS tem o
   * conceito — em serviços, o que impede a exclusão é o item estar EM USO,
   * e isso só a FK sabe.
   */
  protegido?: boolean
}

/**
 * Os dois cards são o mesmo componente porque são a mesma coisa: uma lista
 * global de chips, adicionáveis e removíveis. Duplicar o card daria duas
 * interações sutilmente diferentes para a mesma operação.
 */
function CartaoDeCatalogo({
  titulo,
  subtitulo,
  rodape,
  itens,
  podeEditar,
  consulta,
  criar,
  excluir,
  rotuloDeAdicao,
}: {
  titulo: string
  subtitulo: string
  rodape: string
  itens: Item[]
  podeEditar: boolean
  consulta: { isPending: boolean; isError: boolean; error: unknown }
  criar: {
    mutate: (rotulo: string, opcoes?: { onSuccess?: () => void }) => void
    isPending: boolean
    isError: boolean
    error: unknown
  }
  excluir: { mutate: (id: string) => void; isPending: boolean; isError: boolean; error: unknown }
  rotuloDeAdicao: string
}) {
  // `protegido` é opcional: o catálogo de SERVIÇOS não tem o conceito — lá
  // qualquer item pode ser excluído (o que a FK barra é o item EM USO, e isso o
  // servidor responde com 409 CATALOGO_EM_USO).

  const [adicionando, setAdicionando] = useState(false)
  const [rotulo, setRotulo] = useState('')

  return (
    <div className="border-border-subtle bg-surface-card rounded-card border p-5">
      <h2 className="type-h4 text-text-primary">{titulo}</h2>
      <p className="text-text-muted type-sm">{subtitulo}</p>

      {consulta.isPending && <EsqueletoDeChips rotulo="Carregando catálogo…" className="mt-4" />}
      {consulta.isError && <EstadoDeErro erro={consulta.error} />}

      <ul className="mt-4 flex flex-wrap items-center gap-2">
        {itens.map((item) => (
          // `group` + `group-hover` reproduz o hover do shot: o chip fica opaco e
          // a lixeira aparece. A lixeira NÃO some do DOM — ela é invisível até o
          // hover, mas continua alcançável por teclado, senão excluir viraria uma
          // operação exclusiva de quem usa mouse.
          <li key={item.id} className="group relative flex items-center">
            <span className="duration-fast group-hover:opacity-60">{item.chip}</span>
            {podeEditar && !item.protegido && (
              <button
                type="button"
                aria-label={`Excluir ${item.rotulo}`}
                disabled={excluir.isPending}
                onClick={() => excluir.mutate(item.id)}
                className="text-danger-700 duration-fast absolute -top-2 -right-2 rounded-full bg-white p-1 opacity-0 shadow group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-50"
              >
                <i className="ph ph-trash text-sm" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}

        {podeEditar && !adicionando && (
          <li>
            <button
              type="button"
              onClick={() => setAdicionando(true)}
              className="rounded-control border-border-subtle text-text-muted hover:border-border-strong hover:text-text-body duration-fast inline-flex items-center gap-1.5 border border-dashed px-3 py-1.5 type-sm"
            >
              <i className="ph ph-plus" aria-hidden="true" />
              adicionar
            </button>
          </li>
        )}
      </ul>

      {adicionando && (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(evento) => {
            evento.preventDefault()
            criar.mutate(rotulo, {
              onSuccess: () => {
                setRotulo('')
                setAdicionando(false)
              },
            })
          }}
        >
          <Entrada
            required
            aria-label={rotuloDeAdicao}
            placeholder={rotuloDeAdicao}
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
            className="max-w-xs"
          />
          <Botao variante="primaria" type="submit" disabled={criar.isPending}>
            {criar.isPending ? 'salvando…' : 'salvar'}
          </Botao>
          <Botao variante="discreta" onClick={() => setAdicionando(false)}>
            cancelar
          </Botao>
        </form>
      )}

      {criar.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={criar.error} />
        </div>
      )}
      {excluir.isError && (
        <div className="mt-3">
          <EstadoDeErro erro={excluir.error} />
        </div>
      )}

      <p className="text-text-muted mt-4 type-sm">{rodape}</p>
    </div>
  )
}
